import { DetailsList, IColumn, SelectionMode, TooltipHost } from '@fluentui/react';
import { FC, useMemo } from 'react';
import { IConnectedConversation } from '../../models/reports.internal';
import useSortedListItems from '../../shared/hooks/useSortedListItems';
import ElapsedTimeTooltipHost from './ElapsedTimeTooltipHost';

const ConnectedConversations: FC<{ items: IConnectedConversation[] }> = ({ items }) => {
    const { sortedItems, columnSortProps } = useSortedListItems(items, 'connectionStartTime');

    const columns = useMemo<IColumn[]>(
        () => [
            {
                key: 'connectedTime',
                name: 'Connected time',
                minWidth: 120,
                maxWidth: 120,
                onRender: ({ connectionStartTime }: IConnectedConversation) => <ElapsedTimeTooltipHost timeStart={connectionStartTime} />,
                ...columnSortProps('connectionStartTime'),
            },
            {
                key: 'customerName',
                name: 'Customer name',
                minWidth: 150,
                onRender: ({ customer }: IConnectedConversation) => {
                    const name = customer?.name ?? 'Unknown';
                    return <TooltipHost content={name}>{name}</TooltipHost>;
                },
                ...columnSortProps({ key: 'customerName', selector: i => i.customer ? i.customer.name : null }),
            },
            {
                key: 'customerIdentifier',
                name: 'Customer identifier',
                minWidth: 150,
                maxWidth: 150,
                onRender: ({ customer }: IConnectedConversation) => {
                    const identifier = customer?.identifier ?? '';
                    return <TooltipHost content={identifier}>{identifier}</TooltipHost>;
                },
                ...columnSortProps({ key: 'customerIdentifier', selector: i => i.customer ? i.customer.identifier : null }),
            },
            {
                key: 'agents',
                name: 'Agents',
                minWidth: 200,
                onRender: ({ agents }: IConnectedConversation) => {
                    const names = agents.map(a => a.name).join(', ');
                    return <TooltipHost content={names}>{names}</TooltipHost>;
                },
            },
            {
                key: 'type',
                name: 'Type',
                minWidth: 100,
                maxWidth: 100,
                onRender: ({ virtualConversationType }: IConnectedConversation) => {
                    const type = virtualConversationType ?? 'Inbound';
                    return <TooltipHost content={type}>{type}</TooltipHost>;
                },
                ...columnSortProps('virtualConversationType'),
            },
        ],
        [columnSortProps],
    );

    return (
        <DetailsList
            selectionMode={SelectionMode.none}
            items={sortedItems}
            columns={columns}
            onRenderDetailsFooter={() => items.length === 0 ? <div style={{ textAlign: 'center' }}>No conversations to show</div> : <span />}
            styles={{ root: { minWidth: '960px !important', height: '600px !important' } }}
        />
    );
};

export default ConnectedConversations;